import * as React from 'react';

import classNames from 'classnames';

import {ActionMenu} from 'shells/action-menu/action-menu';
import {Pencil, TrashIcon, CalendarFollowUpIcon} from 'shells/icon';
import {TruncatedGroup} from 'shells/limited-group/truncated-group';
import {ActivityDateAndTime} from 'shells/row/activity-row/activity-date-and-time';

import type {
    GetActivityForModal_activity as Activity,
    ActivityUpdateInput,
    ActivityDeleteInput,
} from 'nutshell-graphql-types';

import {appHistory} from '../../history';

import {getMappedParticipants} from '../core-entity/widgets/activities-widget/helpers';

import {useActivityModalContext} from './activity-modal-context';
import {DeleteConfirmationDialog} from './delete-confirmation-dialog';
import {useActivityCreateOrMergeContactFromExternalAttendee} from './graphql/hooks/use-activity-create-or-merge-contact-from-external-attendee';
import {getHeaderTitle} from './helpers';
import {AttendeeToContactCreatePopover} from './tabs/calendar/attendee-to-contact-create-popover';
import type {ExternalAttendeeToContactData} from './tabs/calendar/participants-section';
import type {UpdateLocation} from './types';

import styles from './activity-modal.module.css';

type Props = {
    activity: Activity | null | undefined;
    handleUpdateActivity: (
        input: ActivityUpdateInput,
        location: UpdateLocation,
        shouldSetHasChanged?: boolean
    ) => Promise<any>;
    handleDeleteActivity: (arg1: ActivityDeleteInput) => Promise<any>;
    setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
    isReadOnly: boolean;
};

export function Header(props: Props) {
    const {activity} = props;

    const [isEditingTitle, setIsEditingTitle] = React.useState<boolean>(!activity);
    const [activityTitle, setActivityTitle] = React.useState<string>('');
    const [isConfirmingRemove, setIsConfirmingRemove] = React.useState<boolean>(false);
    const [externalAttendeeData, setExternalAttendeeData] = React.useState<
        ExternalAttendeeToContactData | null | undefined
    >(null);
    const [popoverAnchor, setPopoverAnchor] = React.useState<HTMLElement | null | undefined>(null);

    const removeRef = React.useRef<HTMLElement | null | undefined>(null);

    const {title: contextTitle, setTitle: setContextTitle} = useActivityModalContext();

    const {
        activityCreateOrMergeContactFromExternalAttendee,
        isLoading: isCreatingOrMergingContact,
    } = useActivityCreateOrMergeContactFromExternalAttendee();

    // Disable editing the title if the activity was created via Scheduler
    const editTitleDisabled = (activity && activity.hasBooking) || props.isReadOnly || false;

    const participants = activity ? getMappedParticipants(activity.participants) : [];

    const handleCreateContactFromExternalAttendee = React.useCallback(
        (data: ExternalAttendeeToContactData) => {
            if (!activity) {
                return;
            }

            activityCreateOrMergeContactFromExternalAttendee({
                activityId: activity.id,
                externalAttendeeId: data.externalAttendeeId,
                name: data.name,
                email: data.email,
                phone: data.phone,
                contactId: data.contactId,
            }).then((result) => {
                if (
                    result.data &&
                    result.data.activityCreateOrMergeContactFromExternalAttendee &&
                    result.data.activityCreateOrMergeContactFromExternalAttendee.contact
                ) {
                    // Close the popover
                    setExternalAttendeeData(null);
                    setPopoverAnchor(null);
                }
            });
        },
        [activity, activityCreateOrMergeContactFromExternalAttendee]
    );

    const handleClickParticipant = React.useCallback(
        (participant, element: HTMLElement) => {
            // Only handle external attendees and when not in read-only mode
            if (participant.type === 'activityExternalAttendees' && !props.isReadOnly && activity) {
                setExternalAttendeeData({
                    externalAttendeeId: participant.id,
                    name: participant.name,
                    email: participant.email,
                    phone: participant.phone,
                });
                setPopoverAnchor(element);
            }
        },
        [props.isReadOnly, activity]
    );

    const handleScheduleFollowUp = React.useCallback(() => {
        if (activity) {
            const hash = `#/create/activities/${activity.id}`;
            appHistory.replace({...appHistory.location, hash: hash, state: {modal: true}});
        }
    }, [activity]);

    const actionMenuOptions = React.useMemo(() => {
        const options = [
            {
                label: 'Rename activity',
                icon: <Pencil size={16} />,
                handleSelect: () => setIsEditingTitle(true),
                isDisabled: editTitleDisabled,
            },
        ];

        if (activity && activity.canScheduleFollowUp) {
            options.push({
                label: 'Schedule follow-up',
                icon: <CalendarFollowUpIcon size={16} />,
                handleSelect: handleScheduleFollowUp,
                isDisabled: !activity || !activity.canScheduleFollowUp,
            });
        }

        if (activity) {
            options.push({
                label: 'Delete activity',
                icon: <TrashIcon size={16} />,
                handleSelect: () => setIsConfirmingRemove(true),
                isDisabled: !activity,
            });
        }

        return options;
    }, [editTitleDisabled, activity, handleScheduleFollowUp]);

    React.useEffect(() => {
        if (activity) {
            setActivityTitle(activity.name);
        }
    }, [activity]);

    // @ts-expect-error
    const handleChangeTitle = (value) => {
        props.setHasUnsavedChanges(true);
        if (activity) {
            setActivityTitle(value);
        } else {
            setContextTitle(value);
        }
    };

    const handleTitleClick = () => {
        if (editTitleDisabled) {
            return;
        }
        setIsEditingTitle(true);
    };

    const handleStopEditing = () => {
        setIsEditingTitle(false);
    };

    const handleSaveClicked = (value: string) => {
        if (activity) {
            props.handleUpdateActivity({activity: activity.id, name: value}, 'header', false);
            setActivityTitle(value);
        }
        setIsEditingTitle(false);
    };

    const onDeleteActivity = (removeSyncedCalendar: boolean) => {
        if (activity) {
            props.handleDeleteActivity({
                activity: activity.id,
                removeFromSyncedCalendar: removeSyncedCalendar,
            });
        }
    };

    let headerTitleDataTip = '';
    if (activity && activity.hasBooking) {
        headerTitleDataTip = 'Changes to the title must be done through Scheduler';
    } else if (activity && !activity.isEditable) {
        headerTitleDataTip =
            'This activity is connected to an external calendar and cannot be edited.<br />Reconnect the associated calendar or allow guests to modify the event.';
    }

    const titleButtonStyleName = classNames(styles['activity-modal-header-title'], {
        [styles['activity-modal-header-title-disabled']]: editTitleDisabled,
    });

    const headerStyleName = classNames(styles['activity-modal-header'], {
        [styles['activity-modal-header-new']]: !activity,
    });

    return (
        <div className={headerStyleName}>
            <div className='flex justify-sb align-start full-width full-height'>
                <div className='flex flex-dir-col full-width full-height'>
                    {getHeaderTitle(
                        !activity,
                        activity ? activityTitle : contextTitle,
                        isEditingTitle,
                        titleButtonStyleName,
                        headerTitleDataTip,
                        handleTitleClick,
                        handleChangeTitle,
                        handleStopEditing,
                        handleSaveClicked
                    )}
                    {activity && (
                        <div className='flex flex-dir-col gap-6'>
                            <ActivityDateAndTime
                                startTime={activity.startTime}
                                endTime={activity.endTime}
                                isAllDay={activity.isAllDay}
                                isOverdue={activity.isOverdue}
                            />
                            <TruncatedGroup
                                participants={participants}
                                maxWidth={740}
                                onClickParticipant={handleClickParticipant}
                            />
                        </div>
                    )}
                </div>
                {!isEditingTitle && (
                    <div className='mt-4'>
                        <ActionMenu
                            options={actionMenuOptions}
                            size='small'
                            padding='less'
                            hasDarkHoverBackground={true}
                            getRef={(ref) => {
                                removeRef.current = ref;
                            }}
                        />
                        {isConfirmingRemove ? (
                            <DeleteConfirmationDialog
                                anchor={removeRef.current}
                                onDeleteActivity={onDeleteActivity}
                                onCancel={() => setIsConfirmingRemove(false)}
                                isReadOnly={props.isReadOnly}
                                isConnectedToExternalCalendar={
                                    activity ? activity.isConnectedToExternalCalendar : false
                                }
                            />
                        ) : undefined}
                    </div>
                )}
            </div>
            {externalAttendeeData && popoverAnchor && (
                <AttendeeToContactCreatePopover
                    anchor={popoverAnchor}
                    onClose={() => {
                        setExternalAttendeeData(null);
                        setPopoverAnchor(null);
                    }}
                    onSubmit={handleCreateContactFromExternalAttendee}
                    data={externalAttendeeData}
                    isSubmitting={isCreatingOrMergingContact}
                />
            )}
        </div>
    );
}
